import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { fmt } from '../../lib/formatters';
import type { Flecha, Reposicion, StockMinimo, UnidadVenta } from '../../../shared/rotacion';

/**
 * Tarjeta bajo la foto del producto (ficha y vista rápida): venta media del
 * subtipo, venta media del producto con su flecha (verde si vende más, amarilla
 * dentro de ±banda %, roja si vende menos) y el puesto en el ranking del
 * subtipo. Semanal o mensual según el cliente. Datos: GET /productos/:id/rotacion.
 *
 * La comparación usa la «venta media con stock» (solo semanas / meses con el
 * stock por encima del mínimo) y la media del subtipo solo cuenta VIGENTES.
 */

type Rotacion = {
  unidad: UnidadVenta | null;
  banda_pct: number;
  producto: { media: number | null; media_prev: number | null; media_con_stock: number | null; vigente: boolean };
  subtipo: { nombre: string | null; media: number | null; productos_con_venta: number; posicion: number | null } | null;
  flecha: Flecha | null;
  stock_minimo: StockMinimo;
  reposicion: Reposicion | null;
  plazo_semanas: number;
};

const FUENTE_MINIMO: Record<StockMinimo['fuente'], string> = { producto: 'de la ficha', condicion: 'condición comercial', global: 'global' };

function IconoFlecha({ f }: { f: Flecha | null }) {
  if (f === 'arriba') {
    return <svg viewBox="0 0 16 16" className="h-4 w-4 text-ok-600" aria-label="Mejor que su subtipo" role="img"><path d="M8 2 14 10H10v4H6v-4H2z" fill="currentColor" /></svg>;
  }
  if (f === 'abajo') {
    return <svg viewBox="0 0 16 16" className="h-4 w-4 text-bad-600" aria-label="Peor que su subtipo" role="img"><path d="M8 14 2 6h4V2h4v4h4z" fill="currentColor" /></svg>;
  }
  if (f === 'neutro') {
    return <svg viewBox="0 0 16 16" className="h-4 w-4 text-warn-500" aria-label="En la media de su subtipo" role="img"><rect x="2" y="6.5" width="12" height="3" rx="1" fill="currentColor" /></svg>;
  }
  return null;
}

export function RotacionSubtipo({ productoId }: { productoId: string }) {
  const q = useQuery({
    queryKey: ['producto', productoId, 'rotacion'],
    queryFn: () => api.get<Rotacion>(`/productos/${productoId}/rotacion`),
    staleTime: 5 * 60_000,
  });
  const d = q.data;
  if (q.isLoading) return <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-400">Calculando rotación…</div>;
  if (!d) return null;
  const u = d.unidad === 'mes' ? 'uds/mes' : 'uds/sem';
  const venta = (v: number | null) => (v == null ? '—' : `${fmt.number(Math.round(v * 10) / 10)} ${u}`);
  const titulo = d.unidad === 'mes' ? 'Venta media mensual' : 'Venta media semanal';

  return (
    <div className="mt-2 space-y-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{titulo}</div>
      {!d.subtipo ? (
        <p className="text-slate-500">Sin subtipo asignado: no hay con quién compararlo.</p>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <div className="min-w-0">
            <div className="truncate text-slate-500" title={d.subtipo.nombre ?? undefined}>Subtipo {d.subtipo.nombre}</div>
            <div className="font-semibold tabular-nums text-slate-800">{venta(d.subtipo.media)}</div>
          </div>
          <div className="min-w-0">
            <div className="text-slate-500">Este producto</div>
            <div className="flex items-center gap-1 font-semibold tabular-nums text-slate-900"
              title={`Solo ${d.unidad === 'mes' ? 'meses' : 'semanas'} con stock por encima del mínimo. Verde: más de un ${d.banda_pct} % por encima de la media del subtipo · amarillo: dentro de ±${d.banda_pct} % · rojo: por debajo`}>
              {d.producto.media_con_stock == null ? <span className="font-normal text-slate-400">sin {d.unidad === 'mes' ? 'meses' : 'semanas'} con stock</span> : venta(d.producto.media_con_stock)} <IconoFlecha f={d.flecha} />
            </div>
          </div>
        </div>
      )}
      {d.subtipo && (
        <div className="text-slate-600">
          {d.subtipo.posicion
            ? <><b className="text-slate-900">{d.subtipo.posicion}º</b> de {d.subtipo.productos_con_venta} vigentes en ventas dentro del subtipo</>
            : !d.producto.vigente
              ? <>No entra en el ranking: solo cuentan los vigentes ({d.subtipo.productos_con_venta} en el subtipo)</>
              : d.subtipo.productos_con_venta > 0
                ? <>Sin ventas con stock en el periodo · {d.subtipo.productos_con_venta} vigentes del subtipo sí venden</>
                : 'Ningún vigente del subtipo tiene ventas con stock en el periodo'}
          <div className="mt-0.5 text-[10px] text-slate-400">Medias con solo {d.unidad === 'mes' ? 'los meses' : 'las semanas'} con stock por encima del mínimo; subtipo, solo vigentes.</div>
        </div>
      )}
      <div className="border-t border-slate-200 pt-1.5 text-slate-500">
        Stock mínimo: <b className="text-slate-700">{fmt.int(d.stock_minimo.valor)}</b> ({FUENTE_MINIMO[d.stock_minimo.fuente]}{d.stock_minimo.detalle ? `: ${d.stock_minimo.detalle}` : ''})
        {d.reposicion && (
          <div className={`mt-1 font-semibold ${d.reposicion.urgente ? 'text-bad-700' : 'text-warn-700'}`}>
            Reponer {fmt.int(d.reposicion.cantidad)} uds: llega al mínimo en {fmt.number(d.reposicion.semanas_hasta_minimo)} semanas (plazo {d.plazo_semanas}).
          </div>
        )}
      </div>
    </div>
  );
}
